import React, { useState, useEffect, useCallback } from 'react'
import { History, Plus, Trash2, FileText, Euro, X, Check, Loader2, Paperclip } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '../../../contexts/AuthContext'

const API = process.env.REACT_APP_BACKEND_URL
const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '--'
const fmtEuro = v => v == null ? null : `${Number(v) > 0 ? '+' : ''}${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €`

const TYPE_COLORS = {
  ajout_vehicule: 'bg-emerald-100 text-emerald-700', retrait_vehicule: 'bg-orange-100 text-orange-700',
  modification_garantie: 'bg-indigo-100 text-indigo-700', modification_franchise: 'bg-purple-100 text-purple-700',
  modification_prime: 'bg-amber-100 text-amber-700', changement_assure: 'bg-blue-100 text-blue-700',
  prorogation: 'bg-teal-100 text-teal-700', resiliation_partielle: 'bg-red-100 text-red-700', autre: 'bg-slate-100 text-slate-600',
}
const inputCls = 'w-full h-9 px-3 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 bg-white'

function AvenantForm({ policeId, tenantId, types, documents, onSaved, onCancel }) {
  const { profile } = useAuth()
  const [saving, setSaving] = useState(false)
  const [f, setF] = useState({ numero_avenant: '', type_avenant: 'autre', date_effet: '', objet: '', detail: '', impact_prime: '', nouvelle_prime_annuelle: '', document_id: '', appliquer_prime: false })
  const set = (k, v) => setF(x => ({ ...x, [k]: v }))

  async function submit() {
    if (!f.date_effet || !f.objet.trim()) { toast.error("Date d'effet et objet requis"); return }
    setSaving(true)
    try {
      const body = {
        tenant_id: tenantId, numero_avenant: f.numero_avenant || null, type_avenant: f.type_avenant, date_effet: f.date_effet,
        objet: f.objet.trim(), detail: f.detail || null,
        impact_prime: f.impact_prime !== '' ? parseFloat(f.impact_prime) : null,
        nouvelle_prime_annuelle: f.nouvelle_prime_annuelle !== '' ? parseFloat(f.nouvelle_prime_annuelle) : null,
        document_id: f.document_id || null, created_by: profile?.id || null, appliquer_prime: f.appliquer_prime,
      }
      const res = await fetch(`${API}/api/polices/${policeId}/avenants`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      const d = await res.json()
      if (!res.ok) throw new Error(d.detail || 'Erreur')
      toast.success('Avenant enregistré')
      onSaved()
    } catch (e) { toast.error(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div className="border-2 border-indigo-200 bg-indigo-50/30 rounded-xl p-4 space-y-3" data-testid="avenant-form">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div><label className="text-xs text-slate-500 mb-1 block">N° avenant</label>
          <input value={f.numero_avenant} onChange={e => set('numero_avenant', e.target.value)} className={inputCls} placeholder="AV-01" data-testid="avenant-numero" /></div>
        <div><label className="text-xs text-slate-500 mb-1 block">Type *</label>
          <select value={f.type_avenant} onChange={e => set('type_avenant', e.target.value)} className={inputCls} data-testid="avenant-type">
            {Object.entries(types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <div><label className="text-xs text-slate-500 mb-1 block">Date d'effet *</label>
          <input type="date" value={f.date_effet} onChange={e => set('date_effet', e.target.value)} className={inputCls} data-testid="avenant-date" /></div>
        <div><label className="text-xs text-slate-500 mb-1 block">Document lié</label>
          <select value={f.document_id} onChange={e => set('document_id', e.target.value)} className={inputCls} data-testid="avenant-document">
            <option value="">— Aucun —</option>
            {documents.map(d => <option key={d.id} value={d.id}>{d.nom_fichier}</option>)}
          </select></div>
      </div>
      <div><label className="text-xs text-slate-500 mb-1 block">Objet *</label>
        <input value={f.objet} onChange={e => set('objet', e.target.value)} className={inputCls} placeholder="Ex : Ajout de 3 tracteurs au parc, franchise DTA portée à 1 500 €..." data-testid="avenant-objet" /></div>
      <div><label className="text-xs text-slate-500 mb-1 block">Détail</label>
        <textarea value={f.detail} onChange={e => set('detail', e.target.value)} rows={2} className="w-full text-sm border border-slate-200 rounded-lg p-2 outline-none focus:ring-2 focus:ring-indigo-500 resize-none" data-testid="avenant-detail" /></div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 items-end">
        <div><label className="text-xs text-slate-500 mb-1 block">Impact prime (± €)</label>
          <input type="number" step="0.01" value={f.impact_prime} onChange={e => set('impact_prime', e.target.value)} className={inputCls} placeholder="+1200" data-testid="avenant-impact" /></div>
        <div><label className="text-xs text-slate-500 mb-1 block">Nouvelle prime annuelle (€)</label>
          <input type="number" step="0.01" value={f.nouvelle_prime_annuelle} onChange={e => set('nouvelle_prime_annuelle', e.target.value)} className={inputCls} data-testid="avenant-nouvelle-prime" /></div>
        <label className="flex items-center gap-2 text-xs text-slate-600 h-9">
          <input type="checkbox" checked={f.appliquer_prime} onChange={e => set('appliquer_prime', e.target.checked)} disabled={f.nouvelle_prime_annuelle === ''} className="rounded" data-testid="avenant-appliquer-prime" />
          Mettre à jour la prime de la police
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-white rounded-lg" data-testid="avenant-cancel"><X size={14} className="inline mr-1" />Annuler</button>
        <button onClick={submit} disabled={saving} className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5" data-testid="avenant-submit">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Enregistrer
        </button>
      </div>
    </div>
  )
}

export default function PoliceAvenantsPanel({ policeId, tenantId, onPoliceUpdate }) {
  const [data, setData] = useState({ avenants: [], documents_avenant: [], types: {} })
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/polices/${policeId}/avenants`)
      if (res.ok) setData(await res.json())
    } finally { setLoading(false) }
  }, [policeId])
  useEffect(() => { load() }, [load])

  async function remove(id) {
    if (!window.confirm('Supprimer cet avenant de l\'historique ?')) return
    await fetch(`${API}/api/polices/${policeId}/avenants/${id}`, { method: 'DELETE' })
    toast.success('Avenant supprimé')
    load()
  }

  const docById = Object.fromEntries((data.documents_avenant || []).map(d => [d.id, d]))

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="police-avenants-panel">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
          <History size={16} className="text-indigo-500" /> Historique des avenants
          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px]" data-testid="avenants-count">{data.avenants.length}</span>
        </h3>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" data-testid="add-avenant-btn">
            <Plus size={13} /> Nouvel avenant
          </button>
        )}
      </div>
      <div className="p-5 space-y-4">
        {showForm && (
          <AvenantForm policeId={policeId} tenantId={tenantId} types={data.types} documents={data.documents_avenant}
            onSaved={() => { setShowForm(false); load(); onPoliceUpdate?.() }} onCancel={() => setShowForm(false)} />
        )}
        {loading ? <Loader2 className="animate-spin text-indigo-400 mx-auto" /> : data.avenants.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4" data-testid="avenants-empty">Aucun avenant enregistré pour cette police.</p>
        ) : (
          <ol className="relative border-l-2 border-slate-200 ml-3 space-y-4" data-testid="avenants-timeline">
            {data.avenants.map(a => {
              const doc = a.document_id ? docById[a.document_id] : null
              return (
                <li key={a.id} className="ml-5 relative" data-testid={`avenant-item-${a.id}`}>
                  <span className="absolute -left-[27px] top-1.5 w-3.5 h-3.5 rounded-full bg-white border-2 border-indigo-500" />
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-slate-800">{fmtDate(a.date_effet)}</span>
                        {a.numero_avenant && <span className="font-mono text-[11px] text-slate-500">{a.numero_avenant}</span>}
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${TYPE_COLORS[a.type_avenant] || TYPE_COLORS.autre}`}>{data.types[a.type_avenant] || a.type_avenant}</span>
                        {a.impact_prime != null && <span className={`text-[11px] font-semibold flex items-center gap-0.5 ${Number(a.impact_prime) >= 0 ? 'text-amber-700' : 'text-emerald-700'}`}><Euro size={10} />{fmtEuro(a.impact_prime)}</span>}
                        {a.nouvelle_prime_annuelle != null && <span className="text-[11px] text-slate-500">→ prime {Number(a.nouvelle_prime_annuelle).toLocaleString('fr-FR')} €</span>}
                      </div>
                      <p className="text-sm text-slate-800 mt-1">{a.objet}</p>
                      {a.detail && <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{a.detail}</p>}
                      {doc && <p className="text-[11px] text-indigo-600 mt-1 flex items-center gap-1"><Paperclip size={10} /> {doc.nom_fichier}</p>}
                    </div>
                    <button onClick={() => remove(a.id)} className="p-1 text-slate-300 hover:text-red-500 shrink-0" title="Supprimer" data-testid={`avenant-delete-${a.id}`}><Trash2 size={13} /></button>
                  </div>
                </li>
              )
            })}
          </ol>
        )}
        {data.documents_non_lies?.length > 0 && (
          <div className="border-t border-slate-100 pt-3">
            <p className="text-[11px] text-slate-400 mb-1.5 flex items-center gap-1"><FileText size={11} /> Documents « Avenant » uploadés non rattachés à une entrée d'historique :</p>
            <div className="flex flex-wrap gap-1.5">
              {data.documents_non_lies.map(d => <span key={d.id} className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px]">{d.nom_fichier} · {fmtDate(d.created_at)}</span>)}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
